import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useServerAccountValidation } from '../hooks/useServerAccountValidation';
import { useUpsertAccountForm } from '../hooks/useUpsertAccountForm';
import type { UpsertAccountDto } from '../types/AccountDto';
import { UpsertAccountForm } from './UpsertAccountForm';

interface NewAccountModalProps {
	close: () => void;
	opened: boolean;
	triggerListUpdate: () => void;
}

export function NewAccountModal({
	opened,
	close,
	triggerListUpdate,
}: NewAccountModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertAccountForm();

	const newAccountMutation = mutate<UpsertAccountDto>(
		`${organisation}/user/add`,
		inputs,
		'multipart/form-data',
	);

	useServerAccountValidation(newAccountMutation, form);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={resetAndClose}
				title={<Title label={'New Account'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newAccountMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Account Added!'}
					body={
						'A new account has been successfully added. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newAccountMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertAccountForm form={form} isEdit={inputs.isEdit} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
